import React from 'react';
import {
  Box,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemText,
  styled,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import numeral from 'numeral';
import { useDropzone } from 'react-dropzone';
import { SdmTypography } from './SdmTypography';
import { Controller } from 'react-hook-form';
import { theme } from 'theme';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';

export const DropZoneStyle = styled('div')(({ theme, disabled = false }) => ({
  outline: 'none',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: '#F4F6F8',
  textAlign: 'center',
  ...(!disabled && {
    '&:hover': { opacity: 0.72, cursor: 'pointer' },
  }),
}));

const SdmDropZone = ({
  files,
  onRemove,
  error,
  sx,
  isMultiple = false,
  disabled = false,
  dropZoneStyle,
  hideUploadedImages = false,
  ...other
}) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      ...other,
      multiple: isMultiple,
      disabled: disabled,
    });

  const getFileData = (file, index) => {
    if (typeof file === 'string') {
      return {
        key: index ? `${file}-${index}` : file,
        preview: file,
      };
    }

    return {
      key: index ? `${file.name}-${index}` : file.name,
      name: file.name,
      size: file.size,
      path: file.path,
      type: file.type,
      preview: file.preview,
      lastModified: file.lastModified,
      lastModifiedDate: file.lastModifiedDate,
    };
  };

  const fData = (number) => {
    return numeral(number).format('0.0 b');
  };

  const acceptedFileItems = files.map((file, index) => {
    const { key, name, size } = getFileData(file, index);
    const hasFile = files.length > 0;
    return (
      <List
        key={key}
        disablePadding
        style={{ ...(hasFile && { margin: '24px 0px' }) }}
      >
        <ListItem
          style={{
            margin: '8px 0px',
            padding: '16px 6px',
            borderRadius: 0.75,
            border: (theme) => `solid 1px ${theme.palette.divider}`,
          }}
        >
          <ListItemText
            primary={typeof file === 'string' ? file : name}
            secondary={typeof file === 'string' ? '' : fData(size || 0)}
            primaryTypographyProps={{ variant: 'subtitle2' }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
          {onRemove && (
            <IconButton edge="end" size="small" onClick={() => onRemove(file)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </ListItem>
      </List>
    );
  });

  return (
    <Box style={{ width: '100%', ...sx }}>
      <DropZoneStyle
        {...getRootProps()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }),
          ...dropZoneStyle,
        }}
        disabled={disabled}
      >
        <input {...getInputProps()} />
        {!disabled ? (
          <>
            <SdmTypography margin={0} variant={'h5'}>
              {i18n.buttons.chooseFile}
            </SdmTypography>
            <SdmTypography
              margin={0}
              variant={'h5Regular'}
              style={{ color: '#637381' }}
            >
              {i18n.buttons.chooseFileSubOne}{' '}
              <span
                style={{
                  color: theme.colors.primary.red,
                  textDecoration: 'underline',
                }}
              >
                {i18n.buttons.chooseFileSubTwo}
              </span>{' '}
              {i18n.buttons.chooseFileSubThree}{' '}
            </SdmTypography>
          </>
        ) : (
          <SdmTypography margin={0} variant={'h5'}>
            {i18n.buttons.notIntFile}
          </SdmTypography>
        )}
      </DropZoneStyle>
      {!hideUploadedImages && <aside>{acceptedFileItems}</aside>}
    </Box>
  );
};

const RHFUploadMultiFile = ({
  control,
  error,
  name,
  defaultValue,
  disabled = false,
  ...other
}) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || []}
      render={(props) => {
        const checkError = !!error && props.value?.length === 0;

        return (
          <SdmDropZone
            files={props.value}
            error={checkError}
            disabled={disabled}
            helperText={
              checkError && (
                <FormHelperText error style={{ padding: '16px 0' }}>
                  {error?.message}
                </FormHelperText>
              )
            }
            {...other}
          />
        );
      }}
    />
  );
};

export { SdmDropZone, RHFUploadMultiFile };
